import React, { useEffect, useReducer, useRef, useState } from "react";
import { View, Text, TouchableOpacity, TextInput, ActivityIndicator } from "react-native";

// import { DatePicker } from "@s77rt/react-native-date-picker";  // https://www.npmjs.com/package/@s77rt/react-native-date-picker?activeTab=readme
import DateTimePicker, { useDefaultStyles as dtpUseDefaultStyles } from "react-native-ui-datepicker";  // https://www.npmjs.com/package/react-native-ui-datepicker
import { Feather } from "@react-native-vector-icons/feather";
import { Picker } from "@react-native-picker/picker";

import Button from "components/Partials/Buttons";
// import DatePicker from "components/Partials/DatePicker";
import PatientProfileImage from "components/Partials/PatientProfileImage.js";
import { deepCopy, objectHash, stateReducer } from "components/helpers/stateReducer";
import { triggerAlert } from "components/Partials/AlertModal";

const Appointments = () => {
  const currentViewObj = window.main.lib.getDefaultViewObj();
  const _theme = window.main.theme;
  const userProfile = window.main.lib.getUserProfile();

  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  const [apptsViewState, apptsViewSetState] = useReducer(stateReducer, {
    locationsData: [],
    appointmentTypes: [],
    patientsData: [],
    appointmentsData: [],
    appointmentTimeSlotsData: [],
    showAppointmentTimeSlots: false,
    initComplete: false,
    selectedAppointmentId: 0,
    selectedPatientId: 0,
  });

  const blankApptObj = useRef({
    appointment_id: 0,
    appointment_title: "",
    appointment_type: "",
    appointment_type_id: 0,
    appointment_duration: 0,
    client_id: 0,
    contact_id: 0,
    first_name: "",
    last_name: "",
    patient_id: 0,
    patient_name: "",
    type_loc_id: 0,
    location_id: "",
    location_name: "",
    location_shortname: "",
    start_date: "",
    end_date: "",
    schedule_id: 1,
    resource_id: "",
    room_title: "",
  });

  const cachedExistingAppt = useRef(blankApptObj.current);

  const [editingApptState, editingApptSetState] = useState(blankApptObj.current);
  const editingApptUpdateState = (obj)=>{
    if (!!Object.keys(obj).length) {
      return editingApptSetState((prev)=>({...prev,...obj}));
    }
  };
  const resetEditingApptState = ()=>{
    // ⬇ Clear Appt
    editingApptSetState(blankApptObj.current);
    // ⬇ Clear patient & appointment_id
    apptsViewSetState([
      { key: "selectedPatientId", type: "number", selectedPatientId: 0 },
      { key: "selectedAppointmentId", type: "number", selectedAppointmentId: 0 }
    ]);
  };

  const [datePickerOpenState, datePickerOpenSetState] = useState(false);
  const toggleDatePickerOpen = ()=>{
    apptDateInputRef.current.blur();
    datePickerOpenSetState(prev=>(!prev));
  };

  const initialDataLoad = async () => {
    try {
      setLoadingPage(true);
      const _stateProps = [
        { key: "initComplete", type: "boolean", initComplete: true }
      ]
      const [locationsResp, typesResp, patientsResp, apptsResp] = await Promise.all([
        window.main.axios.get(window.main.api.getLocations),
        window.main.axios.get(window.main.api.getAppointentTypes),
        window.main.axios.get(window.main.api.getPatients),
        window.main.axios.get(window.main.api.getAppointments),
      ]);
      if (!!locationsResp && !locationsResp.data) {
        throw locationsResp;
      } else {
        _stateProps.push({ key: "locationsData", type: "array", locationsData: locationsResp.data });
      }
      if (!!typesResp && !typesResp.data) {
        throw typesResp;
      } else {
        _stateProps.push({ key: "appointmentTypes", type: "array", appointmentTypes: typesResp.data });
      }
      if (!!patientsResp && !patientsResp.data) {
        throw patientsResp;
      } else {
        _stateProps.push({ key: "patientsData", type: "array", patientsData: patientsResp.data });
      }
      if (!!apptsResp && !apptsResp.data) {
        throw apptsResp;
      } else {
        _stateProps.push({ key: "appointmentsData", type: "array", appointmentsData: apptsResp.data });
      }
      // Is 'appointment_id' provided?
      if (!!Number(currentViewObj.viewParams.appointment_id)) {
        const _appt_id = Number(currentViewObj.viewParams.appointment_id);
        const _match = (apptsResp.data.find(o=>(Number(o.appointment_id)===_appt_id)) || null);
        if (!!_match) {
          editingApptSetState(_match);
          cachedExistingAppt.current = deepCopy(_match);
          _stateProps.push({ key: "selectedAppointmentId", type: "number", selectedAppointmentId: (Number(currentViewObj.viewParams.appointment_id)||0) });
        } else {
          window.main.lib.setCurrentView("appointments");
          return;
        }
      }
      if (!!Number(currentViewObj.viewParams.patient_id)) {
        _stateProps.push({ key: "selectedPatientId", type: "number", selectedPatientId: (Number(currentViewObj.viewParams.patient_id)||0) });
      }
      // Update all states at once:
      apptsViewSetState(_stateProps);
    } catch (err) {
      const errMsg = window.main.lib.parseErrorMessage(err);
      window.main.lib.setStatusState("Error: "+errMsg);
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    initialDataLoad();
  }, []);  // ⬅ Trigger only once on initial page load.

  const handleSubmit = async () => {
    // Validate required fields. Shouldn't have gotten here is not valid along the way, but make sure.
    if (!editingApptState.patient_id) { window.main.lib.setStatusState("Please select a pet."); return;}
    if (!editingApptState.type_loc_id) { window.main.lib.setStatusState("Please select a location."); return; }
    if (!editingApptState.appointment_type_id) { window.main.lib.setStatusState("Please select an appointment type."); return; }
    if (!editingApptState.end_date || !(new Date(editingApptState.end_date)).getHours()) { window.main.lib.setStatusState("Please select a date & time."); return; }
    // Validated. Continue with Submit:
    const payload = deepCopy(editingApptState);
    // payload.start_date = new Date((new Date(payload.start_date)).toISOString().replace(".000Z","")),
    // payload.end_date = new Date((new Date(payload.end_date)).toISOString().replace(".000Z","")),
    window.main.lib.setLoadingState(true);
    try {
      if (!!Number(editingApptState.appointment_id)) {
        await window.main.axios.post(window.main.api.updateAppointment, payload);
        window.main.lib.setStatusState("Appointment Updated!");
      } else {
        await window.main.axios.post(window.main.api.scheduleAppointment, payload);
        window.main.lib.setStatusState("Appointment Scheduled!");
      }
      // const apptsResp = await window.main.axios.get(window.main.api.getAppointments);
      // apptsViewSetState({ key: "appointmentsData", type: "array", appointmentsData: apptsResp.data });
      // resetEditingApptState();
      onClickBackToHome();
    } catch (error) {
      window.main.lib.setStatusState(window.main.lib.parseErrorMessage(error,"An error occurred. Please try again."));
    } finally {
      window.main.lib.setLoadingState(false);
    }
  };

  const onClickCancelAppointment = async () => {
    if (!apptsViewState.selectedAppointmentId) {
      return;
    }
    triggerAlert({
      show: true,
      title: "Cancel Appointment Request",
      message: "Are you sure you want\nto cancel this appointment",
      closeOnTouchOutside: true,
      closeOnHardwareBackPress: true,  // Escape
      showCancelButton: true,
      cancelText: "No",
      onCancelPressed: () => {
        triggerAlert();
      },
      showConfirmButton: true,
      confirmButtonColor: window.main.theme.colors.caution,
      confirmText: "Yes",
      onConfirmPressed: onConfirmCancelAppointment,
    });
  };

  const onConfirmCancelAppointment = async () => {
    if (!apptsViewState.selectedAppointmentId) {
      triggerAlert();
      return;
    }
    window.main.lib.setLoadingState(true);
    try {
      await window.main.axios.post(window.main.api.cancelAppointment, { appointment_id: apptsViewState.selectedAppointmentId });
      // const refreshedAppointmentsResp = await window.main.axios.get(window.main.api.getAppointments);
      // resetEditingApptState();
      // apptsViewSetState({ key: "appointmentsData", type: "array", appointmentsData: refreshedAppointmentsResp.data });
      triggerAlert();
      window.main.lib.setStatusState("You appointment is cancelled.");
      onClickBackToHome();
    } catch (err) {
      window.main.lib.setStatusState(window.main.lib.parseErrorMessage(err));
    } finally {
      window.main.lib.setLoadingState(false);
    }
  };

  const onClickBackToHome = () => {
    window.main.lib.setCurrentView("home");
  };
  const handleCloseAppointments = () => {
    // If an existing appointment is selected, close it but stay on the Create Appt screen.
    if (!!apptsViewState.selectedAppointmentId) {
      resetEditingApptState();
      window.main.lib.setCurrentView("appointments");
      return;
    }
    // Else, navigate back to the HOME screen.
    window.main.lib.setCurrentView("home");
  };

  const onChangeSelectPet = (val) => {
    let _name = "";
    if (!!Number(val)) {
      const _match = apptsViewState.patientsData.find(o=>(Number(o.patient_id)===Number(val)));
      _name = _match.name;
    }
    editingApptUpdateState({patient_id:(Number(val)||0),patient_name:_name});
    apptsViewSetState({ key: "selectedPatientId", type: "number", selectedPatientId: (Number(val)||0) });  // ⬅ Reset Time Slots data
  };

  const onChangeSelectLocation = (val)=>{
    const updateObj = {
      type_loc_id: 0,
      location_id: "",
      location_name: "",
      location_shortname: "",
      appointment_type_id: 0,  // ⬅⬇ Reset Appt-Type fields whenever location is changed to prevent accidental cross-over.
      appointment_type: "",
      appointment_title: "",
      start_date: "",
      end_date: "",           // ⬅ Use null END as flag that no time-slot has been selected.
      schedule_id: 0,
      room_title: "",
      resource_id: "",
    }
    if (!!Number(val)) {
      const _match = apptsViewState.locationsData.find(o=>(Number(o.location_id)===Number(val)));
      updateObj.type_loc_id = _match.location_id;
      updateObj.location_id = `location_${_match.location_id}`;
      updateObj.location_name = _match.name;
      updateObj.location_shortname = _match.lshortname;
    }
    editingApptUpdateState(updateObj);
    apptsViewSetState([
      { key: "appointmentTimeSlotsData", type: "array", appointmentTimeSlotsData: [] },  // ⬅ Reset Time Slots data
      { key: "showAppointmentTimeSlots", type: "boolean", showAppointmentTimeSlots: false },
    ]);
  };

  const onChangeSelectApptType = (val)=>{
    const updateObj = {
      appointment_type_id: 0,
      appointment_type: "",
      appointment_title: "",
      start_date: "",
      end_date: "",         // ⬅ Use null END as flag that no time-slot has been selected.
      schedule_id: 0,
      room_title: "",
      resource_id: "",
    }
    if (!!Number(val)) {
      const _match = apptsViewState.appointmentTypes.find(o=>(Number(o.appointment_type_id)===Number(val)));
      updateObj.appointment_type_id = _match.appointment_type_id;
      updateObj.appointment_type = _match.appointment_type;
      updateObj.appointment_title = (_match.appointment_title || _match.appointment_type);
      updateObj.appointment_duration = _match.duration;
    }
    editingApptUpdateState(updateObj);
    apptsViewSetState([
      { key: "appointmentTimeSlotsData", type: "array", appointmentTimeSlotsData: [] },  // ⬅ Reset Time Slots data
      { key: "showAppointmentTimeSlots", type: "boolean", showAppointmentTimeSlots: false },
    ]);
  };

  const handleDateSelection = (obj) => {
    editingApptUpdateState({
      start_date: new Date(obj.date),   // ⬅ NOTE: TIME on this is '00:00:00'.
      end_date: "",         // ⬅ Use null END as flag that no time-slot has been selected.
      schedule_id: 0,
      resource_id: "",
      room_title: "",
    });
    apptsViewSetState([
      { key: "appointmentTimeSlotsData", type: "array", appointmentTimeSlotsData: [] },  // ⬅ Reset Time Slots data
      { key: "showAppointmentTimeSlots", type: "boolean", showAppointmentTimeSlots: false },
    ]);
    datePickerOpenSetState(false);  // Close date picker after selected.
  };
  const dtpStyles = dtpUseDefaultStyles();
  // dtpStyles.day_cell.minHeight = 35;
  // console.log("dtpStyles =", dtpStyles);

  const handleTimeSelection = (slotObj) => {
    if (!slotObj || !slotObj.slot_start || !slotObj.slot_end) {
      const _start_date = new Date(editingApptState.start_date);
      _start_date.setHours(0).setMinutes(0).setSeconds(0);   // ⬅ Reset TIME on this to '00:00:00'.
      editingApptUpdateState({
        start_date: new Date(_start_date),
        end_date: "",
        schedule_id: 0,
        resource_id: "",
        room_title: "",
      });
      return;
    }
    const _slot_start = new Date(slotObj.slot_start);
    const _start_date = new Date(editingApptState.start_date);
          _start_date.setUTCHours(_slot_start.getHours());
          _start_date.setUTCMinutes(_slot_start.getMinutes());
          _start_date.setUTCSeconds(0);
    const _slot_end = new Date(slotObj.slot_end);
    const _end_date = new Date(_start_date);
          _end_date.setUTCHours(_slot_end.getHours());
          _end_date.setUTCMinutes(_slot_end.getMinutes());
          _end_date.setUTCSeconds(0);
    editingApptUpdateState({
      start_date: new Date(_start_date),
      end_date: new Date(_end_date),
      schedule_id: (Number(slotObj.slot_schedule_id)||0),
      resource_id: (slotObj.slot_resource_id||""),
      room_title: (slotObj.slot_room_title||""),
    });
    apptsViewSetState([
      { key: "showAppointmentTimeSlots", type: "boolean", showAppointmentTimeSlots: false },
    ]);
  };

  const toggleAndFetchOpenAppointments = async () => {
    if (!!apptsViewState.appointmentTimeSlotsData.length && !apptsViewState.showAppointmentTimeSlots) {
      // Show existing data:
      apptsViewSetState([
        { key: "showAppointmentTimeSlots", type: "boolean", showAppointmentTimeSlots: true },
      ]);
      return;
    }
    // Load fresh data AND show options:
    if (!Number(editingApptState.type_loc_id) || !Number(editingApptState.appointment_type_id) || !editingApptState.start_date || ((new Date(editingApptState.start_date)).toString()==="Invalid Date")) {
      window.main.lib.setStatusState("Missing one or more required fields: Location, Type, or Date.");
      return;
    }
    setLoadingAvailability(true);
    try {
      const payload = {
        location_id: editingApptState.type_loc_id,
        appointment_type: editingApptState.appointment_type_id,
        date: (new Date(editingApptState.start_date)).toISOString().split("T")[0],
        appointment_id: (Number(apptsViewState.selectedAppointmentId)||0),
      };
      const openApptsResp = await window.main.axios.post(window.main.api.getAppointentSlots, payload, { timeout: (20*1000) });
      apptsViewSetState([
        { key: "appointmentTimeSlotsData", type: "array", appointmentTimeSlotsData: openApptsResp.data },
        { key: "showAppointmentTimeSlots", type: "boolean", showAppointmentTimeSlots: true },
      ]);
    } catch (err) {
      window.main.lib.setStatusState(window.main.lib.parseErrorMessage(err,"There was an error loading appointment open times. Please try agian."));
    }
    finally {
      setLoadingAvailability(false);
    }
  };

  // ⬇ These 'const' data are reset on each re-render!  These are pre-set flags to keep other code below more brief.
  const cardTileStyle = {..._theme.cardBlock,backgroundColor:_theme.colors.mode.containerBackgroundColor,borderRadius:_theme.borderRadius,padding:24,position:"relative",marginBottom:16,...window.main.theme.boxShadowStyle};
  const fieldLabelStyle = {color:_theme.colors.mode.textColor,fontWeight:"bold",fontSize:15,marginBottom:6};

  const filteredPatientAppts = (!apptsViewState.selectedPatientId ? apptsViewState.appointmentsData : apptsViewState.appointmentsData.filter(appt=>Number(appt.patient_id)===Number(apptsViewState.selectedPatientId)));
  const apptDateInputRef = useRef();

  let showTimeSlotsBtnText = "Check for open time slots";
  if (!!apptsViewState.appointmentTimeSlotsData.length && !!apptsViewState.showAppointmentTimeSlots) {
    showTimeSlotsBtnText   = "Reload open time slots";
  } else if (!!apptsViewState.appointmentTimeSlotsData.length && !apptsViewState.showAppointmentTimeSlots) {
    showTimeSlotsBtnText   = "Show open time slots";
  };
  const isChangeAppttBtnDisabled = (
    !editingApptState.patient_id || !editingApptState.type_loc_id || !editingApptState.appointment_type_id
    || !editingApptState.end_date || ((new Date(editingApptState.end_date)).toString()==="Invalid Date") || (new Date(editingApptState.end_date)).getHours()===0
    || !!apptsViewState.showAppointmentTimeSlots
    || objectHash(editingApptState)===objectHash(cachedExistingAppt.current)
  );

  return (
    <View style={{..._theme.container,borderWidth:0,paddingTop:18,paddingBottom:24,paddingHorizontal:24}}>

      <View style={{..._theme.cardBlock,marginBottom:14}}>
        <Text style={_theme.pageTitleText}>
          Welcome, {(`${userProfile.first_name || ""} ${userProfile.last_name || ""}`).trim() || "Guest"}
        </Text>
      </View>

      <Button ficon="arrow-left" text="Back to My Pets" type="link" onPress={onClickBackToHome} style={{alignSelf:"flex-start",paddingHorizontal:0,marginBottom:10,marginTop:0}} />

      <View style={{...cardTileStyle,zIndex:2}}>
        <TouchableOpacity onPress={handleCloseAppointments} style={{position:"absolute",right:12,top:12,zIndex:10}} accessibilityRole="button" accessibilityLabel="Close appointments">
          <Feather name="x" size={24} color={_theme.colors.mode.textColor} />
        </TouchableOpacity>
        <View style={{width:"100%",flexDirection:"row",alignItems:"center",justifyContent:"space-between",marginBottom:18}}>
          <Text style={_theme.pageTitleText}>
            {!!Number(editingApptState.appointment_id) ? "Change Appointment" : "Request Appointment"}
          </Text>
        </View>

        {loadingPage ? (
          <ActivityIndicator size="small" color={_theme.colors.mode.base} />
        ) : (
          <>
            {!!Number(editingApptState.appointment_id) && (
              <View style={{backgroundColor:"#fff",borderColor:"#e4e4e4",borderRadius:_theme.borderRadius,borderWidth:1,marginBottom:18,paddingHorizontal:14,paddingVertical:12,width:"100%"}}>
                <Text style={{ fontWeight: "bold", fontSize: 15, marginBottom: 4 }}>
                  Current Appointment
                </Text>
                <Text style={{ color: "#333", fontSize: 14 }}>
                  {getAppointmentCardText(cachedExistingAppt.current)}
                </Text>
              </View>
            )}

            <View style={{marginBottom:18,height:100}}>
              <Text style={fieldLabelStyle}>
                Appointment For
              </Text>
              {!!editingApptState.patient_id ? (
                <View style={{width:"100%",backgroundColor:"#ffffff",borderRadius:_theme.borderRadius,paddingVertical:12,paddingHorizontal:14,flexDirection:"row",alignItems:"center",justifyContent:"space-between",...window.main.theme.boxShadowStyle}}>
                  <View style={{alignItems:"center",display:"flex",flexDirection:"row"}}>
                    <PatientProfileImage patientObj={editingApptState} />
                    <Text style={{fontSize:16,fontWeight:"bold",color:"#222222"}}>
                      {editingApptState.patient_name || "[Selected Pet]"}
                    </Text>
                  </View>
                  {apptsViewState.patientsData.length > 1 && (
                    <Button type="link" text="Change" style={{marginHorizontal:0,paddingHorizontal:0,width:"auto"}} textStyle={{fontWeight:"bold"}} onPress={onChangeSelectPet} />
                  )}
                </View>
              ) : (
                <View>
                  <Picker selectedValue={editingApptState.patient_id} onValueChange={onChangeSelectPet} style={_theme.pickerInput}>
                    <Picker.Item label="-- Select One --" value="" />
                    {apptsViewState.patientsData.map((pet) => (
                      <Picker.Item key={`pet_${pet.patient_id}`} label={pet.name} value={Number(pet.patient_id)}/>
                    ))}
                  </Picker>
                </View>
              )}
            </View>

            <View style={{marginBottom:18}}>
              <Text style={fieldLabelStyle}>Location</Text>
              <View>
                <Picker selectedValue={editingApptState.type_loc_id} onValueChange={onChangeSelectLocation} style={_theme.pickerInput}>
                  <Picker.Item label="-- Select One --" value="0" />
                  {apptsViewState.locationsData.map((loc) => (
                    <Picker.Item key={`loc_${loc.location_id}`} label={loc.name} value={loc.location_id} />
                  ))}
                </Picker>
              </View>
            </View>

            <View style={{marginBottom:18}}>
              <Text style={fieldLabelStyle}>Appontment Type</Text>
              <View>
                {!editingApptState.type_loc_id ? (
                  <Text style={{..._theme.textInput,..._theme.cursorBlocked,backgroundColor:"#f1f1f1",color:"#777777",height:33,fontSize:14}}>
                    * Select a Location.
                  </Text>
                ) :  (
                  <Picker selectedValue={editingApptState.appointment_type_id} onValueChange={onChangeSelectApptType} style={_theme.pickerInput}>
                    <Picker.Item label="-- Select One --" value="0" />
                    {apptsViewState.appointmentTypes.filter(o=>(o.type_loc_id===editingApptState.type_loc_id)).map((type) => (
                      <Picker.Item key={`appt_type_${type.appointment_type_id}`} label={type.appointment_type} value={type.appointment_type_id} />
                    ))}
                  </Picker>
                )}
              </View>
            </View>

            <View style={{marginBottom:18,width:"100%",zIndex:10}}>
              <Text style={fieldLabelStyle}>Date</Text>
              <TextInput ref={apptDateInputRef} value={(new Date(editingApptState.start_date)).toString()!=="Invalid Date" ? window.main.lib.dateFormat(editingApptState.start_date) : ""} placeholder="-- Select Date --" onFocus={toggleDatePickerOpen} editable={false} style={{..._theme.textInput,width:"100%"}} />
              {!!datePickerOpenState &&
                <View style={{position:"relative",width:"100%"}}>
                  <View style={{position:"absolute",top:0,left:0,backgroundColor:"#ffffff",borderRadius:_theme.borderRadius,...window.main.theme.boxShadowStyle}}>
                    <DateTimePicker mode="single" date={(new Date(editingApptState.start_date)).toString()!=="Invalid Date" ? window.main.lib.dateFormat(editingApptState.start_date) : undefined} onChange={handleDateSelection} styles={dtpStyles} />
                  </View>
                </View>
              }
            </View>

            <View style={{ marginBottom: 18,width:"100%"}}>
              <Text style={fieldLabelStyle}>Time</Text>

              {!apptsViewState.showAppointmentTimeSlots && !!editingApptState.resource_id && !!editingApptState.end_date &&
                <View style={{..._theme.textInput,width:"100%",marginBottom:12,backgroundColor:"#dff3b4",borderColor:"#8abf3a"}}>
                  <Text style={{color:"#333333",fontSize:"0.7rem",width:"100%"}}>{editingApptState.room_title}</Text>
                  <Text style={{color:"#222222",fontWeight:"500",fontSize:16}}>
                    {`${window.main.lib.timeFormat(editingApptState.start_date)} - ${window.main.lib.timeFormat(editingApptState.end_date)}` + (!!editingApptState.dropoff_time?` (Drop off ${editingApptState.dropoff_time}`:"")}
                  </Text>
                </View>
              }

              {(!!loadingAvailability) ? (
                <ActivityIndicator size="small" color={_theme.colors.mode.base} />
              ) : (!editingApptState.patient_id) ? (
                <Text style={{..._theme.textInput,..._theme.cursorBlocked,backgroundColor:"#f1f1f1",color:"#777777",height:33,fontSize:14}}>
                  * Select a Pet.
                </Text>
              ) : (!editingApptState.type_loc_id) ? (
                <Text style={{..._theme.textInput,..._theme.cursorBlocked,backgroundColor:"#f1f1f1",color:"#777777",height:33,fontSize:14}}>
                  * Select a Location.
                </Text>
              ) : (!editingApptState.start_date) ? (
                <Text style={{..._theme.textInput,..._theme.cursorBlocked,backgroundColor:"#f1f1f1",color:"#777777",height:33,fontSize:14}}>
                  * Select a Date.
                </Text>
              ) : (
                <>
                  <Button text={showTimeSlotsBtnText} type="secondary"  style={{width:"100%",marginBottom:16}} onPress={toggleAndFetchOpenAppointments} />
                  {!!apptsViewState.showAppointmentTimeSlots &&
                    <>
                      {(!!apptsViewState.appointmentTimeSlotsData && apptsViewState.appointmentTimeSlotsData.length===0) &&
                        <Text style={{..._theme.textInput,..._theme.cursorBlocked,backgroundColor:"#f1f1f1",color:"#777777",fontSize:14}}>
                          * No times available for this date.
                        </Text>
                      }
                      <View style={{width:"100%"}}>
                        {!!apptsViewState.appointmentTimeSlotsData && apptsViewState.appointmentTimeSlotsData.map((slotOption) => {
                          const isActive = (editingApptState.resource_id===slotOption.slot_resource_id && new Date(editingApptState.start_date).getTime()===new Date(slotOption.slot_start).getTime() && new Date(editingApptState.end_date).getTime()===new Date(slotOption.slot_end).getTime());
                          const _handleTimeSelection = ()=>{handleTimeSelection(slotOption);};
                          return (
                            <TouchableOpacity key={`slot_${slotOption.slot_id}`} onPress={_handleTimeSelection} style={{..._theme.textInput,width:"100%",marginBottom:12,backgroundColor:(isActive?"#dff3b4":"#ffffff"),borderColor:(isActive?"#8abf3a":"#8abf3a")}}>
                              <Text style={{color:"#333333",fontSize:"0.7rem",width:"100%"}}>{slotOption.slot_room_title}</Text>
                              <Text style={{color:"#222222",fontWeight:(isActive?"500":"400"),fontSize:16}}>
                                {`${window.main.lib.timeFormat(slotOption.slot_start)} - ${window.main.lib.timeFormat(slotOption.slot_end)}` + (!!editingApptState.dropoff_time?` (Drop off ${editingApptState.dropoff_time}`:"")}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    </>
                  }
                </>
              )}
            </View>

            {/* Action Buttons */}
            <View style={{width:"100%"}}>
              <Button text={!!apptsViewState.selectedAppointmentId?"Update Appointment":"Request Appointment"} type="primary" size="large" style={{width:"100%",marginBottom:16}} onPress={handleSubmit} disabled={isChangeAppttBtnDisabled} />
              {!!apptsViewState.selectedAppointmentId &&
                <Button text="Cancel Appointment" type="cautionary2" size="large"  style={{width:"100%",marginBottom:16}} onPress={onClickCancelAppointment} />
              }
            </View>

          </>
        )}
      </View>

      {/* {!datePickerOpen && ( */}
        <View style={{..._theme.cardBlock,minHeight:150,zIndex:1}}>
          <Text style={{color:_theme.colors.mode.textColor,fontWeight:"bold",fontSize:18,marginBottom:12}}>
            My Appointments {apptsViewState.selectedPatientId ? `(${filteredPatientAppts.length})` : `(${apptsViewState.appointmentsData.length})`}
          </Text>
          {loadingPage ? (
            <ActivityIndicator size="small" color={_theme.colors.mode.base} />
          ) : (apptsViewState.selectedPatientId?filteredPatientAppts:apptsViewState.appointmentsData).length ? (
            (apptsViewState.selectedPatientId?filteredPatientAppts:apptsViewState.appointmentsData).map((_appt, idx) => {
              const selectAppt = ()=>{
                editingApptSetState(_appt);
                apptsViewSetState([
                  { key: "selectedPatientId", type: "number", selectedPatientId: (Number(_appt.patient_id)||0) },
                  { key: "selectedAppointmentId", type: "number", selectedAppointmentId: (Number(_appt.appointment_id)||0) }
                ]);
              };
              const isCurrent = (Number(_appt.appointment_id)===Number(apptsViewState.selectedAppointmentId));
              return (
                <TouchableOpacity key={`appt_${_appt.appointment_id}`} onPress={selectAppt} style={{width:"100%",minHeight:68,backgroundColor:isCurrent?"#dff3b4":"#ffffff",borderRadius:_theme.borderRadius,marginBottom:14,paddingVertical:16,paddingHorizontal:16,alignItems:"center",justifyContent:"center",borderWidth:isCurrent?1:0,borderColor:isCurrent?"#8abf3a":"transparent",...window.main.theme.boxShadowStyle}}>
                  <Text style={{color:"#222",fontWeight:"600",fontSize:15,textAlign:"center",lineHeight:22, }}>
                    {getAppointmentCardText(_appt)}
                  </Text>
                </TouchableOpacity>
              );
            })
          ) : (
            <Text style={{color:"#777777",fontSize:14}}>
              No appointments found.
            </Text>
          )}
        </View>
      {/* )} */}

    </View>
  );
};

export default Appointments;

export const getAppointmentCardText = (appointment) => {
  const patientName = (!!appointment.patient_name ? appointment.patient_name : "[Pet Name]");
  const startDate = (Number(new Date(appointment.start_date)) ? new Date(appointment.start_date) : null);
  const formattedDate = window.main.lib.dateFormat(startDate);
  const timePart = window.main.lib.timeFormat(startDate);
  const endDate = (Number(new Date(appointment.start_date)) ? new Date(appointment.end_date) : null);
  const durStr = window.main.lib.dateRangeDurationStr(startDate,endDate);
  const locationLabel = (appointment.location_shortname || "[Location]");
  const typeLabel = (appointment.appointment_type || "[Appointment Type]");
  const dropoffLabel = (appointment.dropoff_time ? ` • Drop Off: ${appointment.dropoff_time}` : "");
  return `${patientName}\n${formattedDate} @ ${timePart}${durStr}\n${locationLabel} • ${typeLabel}${dropoffLabel}`;
};
